import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Plus, Trash2, Save } from 'lucide-react';
import { ImageUpload, API_URL } from './shared';

const TierEditor = ({ title, accent, tier, onChange, token }) => {
  const update = (patch) => onChange({ ...tier, ...patch });
  const updateFeature = (idx, value) => {
    const next = [...(tier.features || [])];
    next[idx] = value;
    update({ features: next });
  };
  const removeFeature = (idx) => {
    const next = (tier.features || []).filter((_, i) => i !== idx);
    update({ features: next });
  };
  const addFeature = () => update({ features: [...(tier.features || []), ''] });

  return (
    <div className="bg-white rounded-lg border p-6 space-y-4" data-testid={`tier-editor-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: accent }}>{title}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Name</label>
          <Input value={tier.name || ''} onChange={(e) => update({ name: e.target.value })} data-testid={`tier-${title.toLowerCase()}-name`} />
        </div>
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Price (e.g. R1,000)</label>
          <Input value={tier.price || ''} onChange={(e) => update({ price: e.target.value })} data-testid={`tier-${title.toLowerCase()}-price`} />
        </div>
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Period (e.g. / year)</label>
          <Input value={tier.period || ''} onChange={(e) => update({ period: e.target.value })} data-testid={`tier-${title.toLowerCase()}-period`} />
        </div>
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Price note (optional)</label>
          <Input value={tier.price_note || ''} onChange={(e) => update({ price_note: e.target.value })} data-testid={`tier-${title.toLowerCase()}-pricenote`} />
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-600 mb-1 block">Tagline</label>
        <Input value={tier.tagline || ''} onChange={(e) => update({ tagline: e.target.value })} data-testid={`tier-${title.toLowerCase()}-tagline`} />
      </div>

      <div>
        <label className="text-sm text-gray-600 mb-1 block">Image</label>
        <ImageUpload
          value={tier.image_url || ''}
          onChange={(url) => update({ image_url: url })}
          token={token}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-gray-600">Features (bullets)</label>
          <Button type="button" size="sm" variant="outline" onClick={addFeature} data-testid={`tier-${title.toLowerCase()}-add-feature`}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(tier.features || []).map((f, i) => (
            <div key={`feat-${i}`} className="flex gap-2">
              <Input
                value={f}
                onChange={(e) => updateFeature(i, e.target.value)}
                placeholder="e.g. UP TO 20% discount on all new gear"
                data-testid={`tier-${title.toLowerCase()}-feature-${i}`}
              />
              <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => removeFeature(i)} data-testid={`tier-${title.toLowerCase()}-remove-feature-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MembershipsTab = ({ token }) => {
  const [heading, setHeading] = useState('');
  const [subheading, setSubheading] = useState('');
  const [club, setClub] = useState(null);
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/admin/membership-content`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!mounted) return;
        setHeading(res.data.heading || '');
        setSubheading(res.data.subheading || '');
        setClub(res.data.club);
        setPro(res.data.pro);
      })
      .catch(() => toast.error('Failed to load membership content'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/membership-content`, { heading, subheading, club, pro }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Membership content saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !club || !pro) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="memberships-tab">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Memberships Editor</h2>
          <p className="text-sm text-gray-500">Edit pricing, tagline, image and feature bullets shown on the /membership page.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="memberships-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="bg-white rounded-lg border p-6 space-y-4">
        <h3 className="text-base font-semibold text-[#0A2342]">Page Header</h3>
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Heading</label>
          <Input value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="Join the Just Dive Club" data-testid="memberships-heading-input" />
        </div>
        <div>
          <label className="text-sm text-gray-600 mb-1 block">Subheading</label>
          <Input value={subheading} onChange={(e) => setSubheading(e.target.value)} placeholder="Built for divers who want to dive more, not plan more." data-testid="memberships-subheading-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TierEditor title="Club" accent="#0A2342" tier={club} onChange={setClub} token={token} />
        <TierEditor title="Pro" accent="#FF6B35" tier={pro} onChange={setPro} token={token} />
      </div>
    </div>
  );
};

export default MembershipsTab;
